import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { Report } from "@/types/evaluate";
import { CellStatus } from "@/components/Evaluate/TableComponents/RowCell/Cells/primitives";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

type StatusCounts = Record<CellStatus, number>;

interface ReportStats {
  status_counts: StatusCounts;
}

export interface GetReportResponse {
  success: boolean;
  report: Report;
  stats: ReportStats;
  status: "COMPLETED" | "RUNNING";
}

export const useGetReport = (reportId: number | null) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  return useQuery(
    [ENDPOINTS.legacy_reports, reportId],
    (): Promise<GetReportResponse | null> => {
      if (!reportId) {
        return Promise.resolve(null);
      }
      const url = new URL(`${ENDPOINTS.legacy_reports}/${reportId.toString()}`);
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson;
          }
        });
    },
    {
      refetchInterval: data => {
        if (!data) {
          return 5000;
        }

        if (data.status === "RUNNING") {
          return 5000;
        }

        const hasRunningOrQueuedCells =
          (data.stats?.status_counts?.RUNNING || 0) + (data.stats?.status_counts?.QUEUED || 0) > 0;

        if (data.status === "COMPLETED" && !hasRunningOrQueuedCells) {
          return false;
        }

        return 5000;
      },
    },
  );
};
